import { useEffect, useState, useMemo, useCallback } from 'react'
import { useForm, Controller } from 'react-hook-form'
import {
  Loader2,
  UserPlus,
  CheckCircle2,
  AlertCircle,
  User,
  Phone,
  Heart,
  Activity,
  ShieldCheck,
  Users,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { QuestionRenderer, encodeFieldName } from '@/components/registration/QuestionRenderer'
import { useBeneficiaryRegistration } from '@/hooks/useRegistration'
import { useLocalization } from '@/contexts/LocalizationContext'
import { cn } from '@/lib/utils'
import {
  BENEFICIARY_RELATIONSHIPS,
  type Beneficiary,
} from '@/services/orderApi'
import type { SubmitRegistrationResponse } from '@/types/registration'

// =============================================================================
// Section grouping configuration (same pattern as ClientRegistrationModal)
// =============================================================================

type SectionKey = 'identity' | 'contact' | 'demographics' | 'health' | 'vitals' | 'consent'

const SECTION_ORDER: SectionKey[] = ['identity', 'contact', 'demographics', 'health', 'vitals', 'consent']

const SECTION_META: Record<SectionKey, { en: string; ar: string; icon: React.ElementType }> = {
  identity: { en: 'Identity', ar: 'الهوية', icon: User },
  contact: { en: 'Contact Information', ar: 'معلومات الاتصال', icon: Phone },
  demographics: { en: 'Personal Information', ar: 'المعلومات الشخصية', icon: UserPlus },
  health: { en: 'Health Information', ar: 'المعلومات الصحية', icon: Heart },
  vitals: { en: 'Vital Signs', ar: 'العلامات الحيوية', icon: Activity },
  consent: { en: 'Consent & Agreements', ar: 'الموافقة والاتفاقيات', icon: ShieldCheck },
}

function getQuestionSection(code: string): SectionKey {
  const upper = code.toUpperCase()
  if (upper === 'ID2' || upper === 'ID3') return 'identity'
  if (upper.startsWith('CONTACT')) return 'contact'
  if (upper.startsWith('DEM')) return 'demographics'
  if (upper.startsWith('VS')) return 'vitals'
  if (upper.startsWith('CONSENT')) return 'consent'
  if (
    upper.startsWith('HC') ||
    upper.startsWith('MED') ||
    code.includes('allerg') ||
    code.includes('special_needs')
  ) return 'health'
  return 'health'
}

// =============================================================================
// Relationship selector
// =============================================================================

const RELATIONSHIP_OPTIONS = BENEFICIARY_RELATIONSHIPS.map((v) => ({
  value: v,
  labelEn: v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
  labelAr:
    v === 'child' ? 'طفل' :
    v === 'spouse' ? 'زوج/زوجة' :
    v === 'parent' ? 'والد' :
    v === 'sibling' ? 'أخ/أخت' :
    v === 'self' ? 'نفسه' :
    v === 'grandparent' ? 'جد/جدة' :
    v === 'grandchild' ? 'حفيد' :
    v === 'other_family' ? 'عائلة أخرى' :
    v === 'friend' ? 'صديق' : 'آخر',
}))

// =============================================================================
// Section divider
// =============================================================================

function SectionDivider({ label, icon: Icon }: { label: string; icon: React.ElementType }) {
  return (
    <div className="relative py-2">
      <div className="absolute inset-0 flex items-center">
        <span className="w-full border-t" />
      </div>
      <div className="relative flex justify-center text-xs uppercase">
        <span className="bg-background px-2 text-muted-foreground flex items-center gap-1.5">
          <Icon className="h-3 w-3" />
          {label}
        </span>
      </div>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export interface AddBeneficiaryModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: (beneficiary: Beneficiary) => void
  clientId?: string | null
  /** When set, registration links the beneficiary to this business client profile */
  businessClientProfileId?: string | null
}

export function AddBeneficiaryModal({
  isOpen,
  onClose,
  onSuccess,
  clientId,
  businessClientProfileId,
}: AddBeneficiaryModalProps) {
  const { language, t } = useLocalization()
  const isArabic = language === 'ar'

  const {
    survey,
    questions,
    isLoadingSurvey,
    surveyError,
    loadSurvey,
    refetchSurvey,
    submitAsync,
    isSubmitting,
    submitError,
    registrationResult,
    reset: resetMutation,
  } = useBeneficiaryRegistration()

  const [createdBeneficiary, setCreatedBeneficiary] = useState<Beneficiary | null>(null)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const form = useForm<Record<string, any>>({
    mode: 'onTouched',
  })

  const sortedQuestions = useMemo(() => {
    if (!questions?.length) return []
    return [...questions].sort((a, b) => a.order - b.order)
  }, [questions])

  const groupedSections = useMemo(() => {
    const sectionMap = new Map<SectionKey, typeof sortedQuestions>()

    for (const question of sortedQuestions) {
      const section = getQuestionSection(question.code)
      if (!sectionMap.has(section)) {
        sectionMap.set(section, [])
      }
      sectionMap.get(section)!.push(question)
    }

    return SECTION_ORDER
      .filter(key => sectionMap.has(key))
      .map(key => ({
        key,
        label: isArabic ? SECTION_META[key].ar : SECTION_META[key].en,
        icon: SECTION_META[key].icon,
        questions: sectionMap.get(key)!,
      }))
  }, [sortedQuestions, isArabic])

  useEffect(() => {
    if (isOpen && !survey && !isLoadingSurvey) {
      loadSurvey()
    }
  }, [isOpen, survey, isLoadingSurvey, loadSurvey])

  const questionCodeMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const q of sortedQuestions) {
      map.set(q.code.toUpperCase(), q.code)
    }
    return map
  }, [sortedQuestions])

  const getActualCode = useCallback(
    (targetCode: string) => questionCodeMap.get(targetCode.toUpperCase()) ?? targetCode,
    [questionCodeMap],
  )

  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const defaults: Record<string, any> = {
        relationship_type: '',
      }
      form.reset(defaults)
      resetMutation()
      setCreatedBeneficiary(null)
    }
  }, [isOpen, form, resetMutation])

  const handleClose = useCallback(() => {
    if (!isSubmitting) onClose()
  }, [isSubmitting, onClose])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) handleClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, isSubmitting, handleClose])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = async (formData: Record<string, any>) => {
    if (!survey?.id) {
      console.warn('No survey ID available')
    }

    try {
      const val = (targetCode: string) =>
        formData[encodeFieldName(getActualCode(targetCode))] ?? ''

      const fullName = String(val('ID2')).trim()
      const nameParts = fullName.split(/\s+/)
      const firstName = nameParts[0] || ''
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : ''

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const answers: Record<string, any> = {
        phone_number: val('CONTACT1'),
        id_number: val('ID3'),
        email: val('CONTACT2'),
        first_name: firstName,
        last_name: lastName,
        date_of_birth: val('DEM1'),
        gender: val('DEM2'),
      }

      for (const q of sortedQuestions) {
        const fieldKey = encodeFieldName(q.code)
        const value = formData[fieldKey]
        if (value !== undefined && value !== '') {
          answers[q.code] = value
        }
      }

      const relationshipType = formData['relationship_type'] || 'other'

      const result = await submitAsync({
        survey_id: survey?.id || 'default',
        profile_type: 'beneficiary',
        answers,
        ...(businessClientProfileId
          ? { business_client_profile_id: businessClientProfileId }
          : { client_id: clientId || undefined }),
        relationship_type: relationshipType,
      })

      // Prefer beneficiary profile id (order APIs), never the user row id
      const beneficiary: Beneficiary = {
        id: result.profile?.id || '',
        fullName: `${result.user?.first_name || firstName} ${result.user?.last_name || lastName}`.trim(),
        firstName: result.user?.first_name || firstName,
        lastName: result.user?.last_name || lastName,
        relationship: relationshipType,
        dateOfBirth: answers.date_of_birth || undefined,
        gender: answers.gender || undefined,
      }

      setCreatedBeneficiary(beneficiary)

      setTimeout(() => {
        onSuccess?.(beneficiary)
        handleClose()
      }, 1200)
    } catch (error) {
      console.error('Beneficiary registration failed:', error)
    }
  }

  const watchedValues = form.watch()
  const requiredSurveyKeys = sortedQuestions.filter(q => q.required).map(q => encodeFieldName(q.code))
  const allRequiredKeys = [...requiredSurveyKeys, 'relationship_type']
  const filledCount = allRequiredKeys.filter(key => {
    const val = watchedValues[key]
    return val !== undefined && val !== '' && val !== null
  }).length
  const progressPercent = allRequiredKeys.length > 0
    ? Math.round((filledCount / allRequiredKeys.length) * 100)
    : 0

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <UserPlus className="h-4 w-4 text-primary" />
            </div>
            {t('orderCreation.addBeneficiary')}
          </DialogTitle>
          <DialogDescription>
            {t('orderCreation.addBeneficiaryDescription')}
          </DialogDescription>
        </DialogHeader>

        {/* Success State */}
        {(createdBeneficiary || registrationResult) && (
          <div className="flex flex-col items-center justify-center py-12 px-6 space-y-4 animate-in fade-in-50 zoom-in-95">
            <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <div className="text-center space-y-1">
              <p className="text-lg font-semibold text-green-700">
                {t('orderCreation.beneficiaryAddedSuccessfully')}
              </p>
              {createdBeneficiary && (
                <>
                  <p className="text-sm text-muted-foreground">{createdBeneficiary.fullName}</p>
                  <Badge variant="outline" className="mt-2">
                    {createdBeneficiary.relationship}
                  </Badge>
                </>
              )}
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoadingSurvey && !registrationResult && !createdBeneficiary && (
          <div className="flex flex-col items-center justify-center py-12 px-6 space-y-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">
              {t('orderCreation.loadingForm')}
            </p>
          </div>
        )}

        {/* Error State */}
        {surveyError && !registrationResult && !createdBeneficiary && (
          <div className="py-8 px-6 space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t('orderCreation.failedToLoadForm')}
              </AlertDescription>
            </Alert>
            <div className="flex justify-center">
              <Button variant="outline" onClick={() => refetchSurvey()}>
                {t('common.retry')}
              </Button>
            </div>
          </div>
        )}

        {/* Form */}
        {!isLoadingSurvey && !surveyError && !registrationResult && !createdBeneficiary && (
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
            {/* Progress indicator */}
            <div className="px-6 py-3 border-b shrink-0 bg-muted/30">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span>{t('orderCreation.formCompletion')}</span>
                <span>{progressPercent}%</span>
              </div>
              <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            {/* Scrollable Form Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-5">
                {submitError && (
                  <Alert variant="destructive" className="animate-in slide-in-from-top-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="whitespace-pre-line">{submitError}</AlertDescription>
                  </Alert>
                )}

                {/* Dynamic Survey Sections */}
                {groupedSections.map((section) => (
                  <div key={section.key} className="space-y-4">
                    <SectionDivider label={section.label} icon={section.icon} />

                    {section.questions.map((question) => (
                      <QuestionRenderer
                        key={question.code}
                        question={question}
                        form={form}
                        locale={language}
                        disabled={isSubmitting}
                      />
                    ))}
                  </div>
                ))}

                <div className="h-2" />
              </div>
            </div>

            <DialogFooter className="px-6 py-4 border-t shrink-0 gap-2 sm:gap-0 bg-background">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="min-w-[140px]"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin me-2" />
                    {t('orderCreation.adding')}
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 me-2" />
                    {t('orderCreation.addBeneficiary')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
